import React from 'react';
import {
	autorizeClientRequestPortal,
	autorizeRequestPortal,
} from '../../thunks/request_portals_thunks';
import { connect } from 'react-redux';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { Link } from 'react-router-dom';
import RouteWithSubRoutes from '../route_with_sub_routes/RouteWithSubRoutes';
import { Button, Result, Typography } from 'antd';
import { getLinkWIthBackLinkParams, getQueryStringFromObject } from '../../utils/HistoryUtils';
import { CopyOutlined } from '@ant-design/icons';

function generateTitleCopy({ locationName, problemType, areaName, contractorClientName }) {
	let copy = 'Report ';

	if (problemType) {
		copy += `a ${problemType} issue`;
	} else {
		copy += 'an issue';
	}

	if (areaName && locationName) {
		copy += ` at ${areaName} area - ${locationName}`;
	} else if (locationName) {
		copy += ` at ${locationName}`;
	}
	if (contractorClientName) {
		copy += ` at ${contractorClientName}`;
	}

	return copy;
}

function generateDescription({ supplierFacilityName }) {
	let copy = `Click below to report an issue and we'll promptly notify ${
		supplierFacilityName || ''
	} staff`;

	return copy;
}

const DEFAULT_CTA = `Report Issue`;
interface RequestPortalData {
	customerLogo?: string;
	title?: string;
	description?: string;
	locationId?: string;
	areaId?: string;
	contractorClientId?: string;
	problemTypeId?: string;
	locationName?: string;
	areaName?: string;
	supplierFacilityId?: string;
	contractorClientName?: string;
	supplierFacilityName?: string;
	problemTypeName?: string;
	ctaTitle?: string;
	showWOTitle?: boolean;
	isCustomerInitiatedOnly?: boolean;
	defaultPriorityId?: string;
	imagesNotAllowed?: boolean;
	troubleshootingStepsNotAllowed?: boolean;
	forcePendingStatusOnCreate?: boolean;
	portalTheme?: {
		pageBackgroundColor?: string;
		buttonColor?: string;
		titleColor?: string;
		descriptionColor?: string;
		mascotImage?: string;
	};
}

const RequestPortalLandingPage = ({ authorize, authorizeClient, match, childRoutes, location }) => {
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState(null);

	const [data, setData] = React.useState<RequestPortalData>({});

	const customerId = match.params.customerId;
	const locationId = match.params.locationId;
	const areaId = match.params.areaId;
	const problemTypeId = match.params.problemTypeId;
	const clientId = match.params.clientId;
	React.useEffect(() => {
		if (customerId) {
			const action = clientId ? authorizeClient : authorize;
			action({ customerId, locationId, areaId, problemTypeId, clientId })
				.then(setData)
				.catch((err) => setError(err || 'Something went wrong'))
				.finally(() => {
					setLoading(false);
				});
		} else {
			setLoading(false);
		}
	}, []);

	if (loading) {
		return <PageLoadingPlaceholder />;
	}

	if (error || !customerId) {
		return (
			<div className="min-h-screen bg-gray-800">
				<header>
					<div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-6 sm:px-6 md:justify-start md:space-x-10 lg:px-8"></div>
				</header>
				<main>
					<div className="relative">
						<Result
							status="404"
							title={
								<div className="text-center text-4xl font-extrabold text-white  sm:text-5xl lg:text-6xl  ">
									Welcome to the Openwrench Request Portal
								</div>
							}
							subTitle={
								<div>
									<div className="mx-auto mt-4  max-w-lg text-center text-xl text-gray-500 sm:max-w-3xl">
										We're sorry, but the page you're looking for doesn't exist.
									</div>
									<div className="mx-auto mt-4  max-w-lg text-center text-xl text-gray-500 sm:max-w-3xl">
										If you believe this is an error or would like to learn more about how Openwrench
										can support your operations, please don't hesitate to reach out to us at{' '}
										<Typography.Text
											copyable={{
												text: 'support@useopenwrench.com',
												icon: <CopyOutlined style={{ color: 'white' }} />,
											}}
										>
											<span className="text-gray-500">support@useopenwrench.com.</span>
										</Typography.Text>
									</div>
								</div>
							}
						/>
					</div>
				</main>
				<footer className="fixed inset-x-0 bottom-0 ">
					<div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 md:flex md:items-center md:justify-center lg:px-8">
						<nav className="-mx-5 -my-2 flex flex-wrap justify-center" aria-label="Footer">
							<div className="px-5 py-2">
								<span className="text-lg text-white hover:text-gray-300">
									Powered by{' '}
									<img
										className="h-6 w-auto "
										src={
											'https://emptor-data-assets.s3.amazonaws.com/images/openwrench-logos/openwrench-new-logo-white.svg'
										}
										alt=""
									/>
								</span>
							</div>
						</nav>
					</div>
				</footer>
			</div>
		);
	}
	const {
		customerLogo,
		title,
		description,
		locationId: actuallocationId,
		areaId: actualareaId,
		problemTypeId: actualproblemTypeId,
		locationName,
		areaName,
		problemTypeName,
		ctaTitle,
		showWOTitle,
		contractorClientId,
		supplierFacilityId,
		contractorClientName,
		supplierFacilityName,
		isCustomerInitiatedOnly,
		defaultPriorityId,
		imagesNotAllowed,
		troubleshootingStepsNotAllowed,
		forcePendingStatusOnCreate,
		portalTheme,
	} = data || {};
	const pageBackgroundColor = portalTheme && portalTheme.pageBackgroundColor;
	const buttonColor = portalTheme && portalTheme.buttonColor;
	const titleColor = portalTheme && portalTheme.titleColor;
	const descriptionColor = portalTheme && portalTheme.descriptionColor;
	const mascotImage = portalTheme && portalTheme.mascotImage;
	const queryString = getQueryStringFromObject({
		locationId: actuallocationId,
		areaId: actualareaId,
		problemTypeTopLevel: actualproblemTypeId,
		showWOTitle,
		contractorClientId,
		supplierFacilityId: supplierFacilityId,
		isCustomerInitiatedOnly,
		defaultPriorityId,
		imagesNotAllowed,
		troubleshootingStepsNotAllowed,
		forcePendingStatusOnCreate,
	});
	const defaultTitle = generateTitleCopy({
		contractorClientName,
		locationName,
		problemType: problemTypeName,
		areaName,
	});
	const defaultDescription = generateDescription({ supplierFacilityName });
	return (
		<div
			className="min-h-screen bg-gray-800"
			style={pageBackgroundColor ? { backgroundColor: pageBackgroundColor } : undefined}
		>
			<header>
				<div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-6 sm:px-6 md:justify-start md:space-x-10 lg:px-8"></div>
			</header>
			<main>
				<div className="relative">
					<div className="absolute inset-x-0 bottom-0 h-1/2 "></div>
					<div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
						<div className="relative  sm:overflow-hidden sm:rounded-2xl">
							<div className="relative flex flex-col gap-y-6 px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
								<div className="flex justify-center lg:flex-1">
									{customerLogo ? (
										<img className="h-10 w-auto sm:h-10" src={customerLogo} alt="" />
									) : (
										<img
											className="h-8 w-auto sm:h-10"
											src={
												'https://emptor-data-assets.s3.amazonaws.com/images/openwrench-logos/openwrench-new-logo-white.svg'
											}
											alt=""
										/>
									)}
								</div>
								<div className="text-center text-4xl font-extrabold  sm:text-5xl lg:text-6xl">
									<span
										className="block text-white"
										style={titleColor ? { color: titleColor } : undefined}
									>
										{title || defaultTitle}
									</span>
								</div>
								<p
									className="mx-auto  max-w-lg text-center text-xl text-gray-500 sm:max-w-3xl"
									style={descriptionColor ? { color: descriptionColor } : undefined}
								>
									{description || defaultDescription}
								</p>
								<div className="mx-auto mt-2 max-w-sm sm:flex sm:max-w-none sm:justify-center">
									<Link
										to={getLinkWIthBackLinkParams(
											location,
											'Back',
											`/${clientId ? 'client' : 'customer'}/createWorkOrder${
												queryString ? `?${queryString}` : ''
											}`
										)}
									>
										<Button
											type="primary"
											size="large"
											className={buttonColor ? 'transition-opacity hover:opacity-90' : undefined}
											style={
												buttonColor
													? {
															backgroundColor: buttonColor,
															borderColor: buttonColor,
													  }
													: undefined
											}
										>
											{ctaTitle || DEFAULT_CTA}
										</Button>
									</Link>
								</div>
								{mascotImage ? (
									<div className="flex justify-center">
										<img className="h-auto max-h-64 w-auto" src={mascotImage} alt="" />
									</div>
								) : null}
							</div>
						</div>
					</div>
				</div>
			</main>
			<footer className="fixed inset-x-0 bottom-0 ">
				<div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 md:flex md:items-center md:justify-center lg:px-8">
					<nav className="-mx-5 -my-2 flex flex-wrap justify-center" aria-label="Footer">
						<div className="px-5 py-2">
							<span className="text-lg text-white hover:text-gray-300">
								Powered by{' '}
								<img
									className="h-6 w-auto "
									src={
										'https://emptor-data-assets.s3.amazonaws.com/images/openwrench-logos/openwrench-new-logo-white.svg'
									}
									alt=""
								/>
							</span>
						</div>
					</nav>
				</div>
			</footer>

			{childRoutes.map((r, i) => (
				<RouteWithSubRoutes key={i} {...r} />
			))}
		</div>
	);
};

const mapStateToProps = (state) => ({});
const mapDispatchToProps = (dispatch) => ({
	authorize: ({ customerId, locationId, areaId, problemTypeId }) =>
		dispatch(autorizeRequestPortal('')({ customerId, locationId, areaId, problemTypeId })),
	authorizeClient: ({ customerId, locationId, areaId, problemTypeId, clientId }) =>
		dispatch(
			autorizeClientRequestPortal('')({
				customerId,
				locationId,
				areaId,
				problemTypeId,
				clientId,
			})
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(RequestPortalLandingPage);
